import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import { localDateString } from '../../../utils/helpers/localDate';

// Vendor monthly visits: a month-by-month tracker of AMC service visits
// (expected from each AMC's service schedule vs. visits logged) plus the visit
// log itself, including ad-hoc and emergency calls.

const TRACKER_STATUS = {
    DONE: { label: 'Done', cls: 'tw-bg-emerald-100 tw-text-emerald-700', icon: 'mdi:check-circle' },
    PENDING: { label: 'Pending', cls: 'tw-bg-amber-100 tw-text-amber-700', icon: 'mdi:clock-outline' },
    MISSED: { label: 'Missed', cls: 'tw-bg-rose-100 tw-text-rose-700', icon: 'mdi:close-circle' },
    NOT_DUE: { label: 'Not due', cls: 'tw-bg-gray-100 tw-text-gray-500', icon: 'mdi:minus-circle-outline' },
    EXTRA: { label: 'Extra visit', cls: 'tw-bg-sky-100 tw-text-sky-700', icon: 'mdi:plus-circle-outline' },
    AD_HOC: { label: 'Ad-hoc', cls: 'tw-bg-violet-100 tw-text-violet-700', icon: 'mdi:lightning-bolt-outline' },
};
const VISIT_STATUS = {
    COMPLETED: 'tw-bg-emerald-50 tw-text-emerald-700',
    PARTIAL: 'tw-bg-amber-50 tw-text-amber-700',
    MISSED: 'tw-bg-rose-50 tw-text-rose-700',
    CANCELLED: 'tw-bg-gray-100 tw-text-gray-500',
};
const VALID_SCHEDULES = ['Monthly', 'Bi-Monthly', 'Quarterly', 'Half-Yearly', 'Yearly', 'On-Demand'];

const pretty = (v) => String(v || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
const fmtDate = (v) => (v ? new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtTime = (t) => (t ? String(t).slice(0, 5) : '');
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
const shiftMonth = (m, delta) => {
    const [y, mo] = m.split('-').map(Number);
    const d = new Date(y, mo - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const scheduleLabel = (s) => (VALID_SCHEDULES.includes(s) ? s : 'Schedule not set');

const inputClass = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-teal-500';
const labelClass = 'tw-block tw-text-[11px] tw-font-medium tw-text-gray-700 tw-mb-1';

const EMPTY_FORM = {
    vendor_id: '', vendor_amc_id: '', visit_date: '', check_in_time: '', check_out_time: '',
    visit_type: 'SCHEDULED', status: 'COMPLETED', technician_name: '', technician_phone: '',
    work_done: '', issues_found: '', verified_by: '', rating: '', remarks: '',
};

const VisitModal = ({ initial, vendors, onClose, onSaved }) => {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
    const vendor = vendors.find(v => String(v.vendor_id) === String(form.vendor_id));
    const amcs = vendor?.amcs || [];

    const submit = async () => {
        if (!form.vendor_id) return toast.warn('Select a vendor');
        if (!form.visit_date) return toast.warn('Visit date is required');
        if (form.check_in_time && form.check_out_time && form.check_out_time < form.check_in_time) {
            return toast.warn('Time out cannot be before time in');
        }
        setSaving(true);
        try {
            const body = {
                ...form,
                vendor_id: Number(form.vendor_id),
                vendor_amc_id: form.vendor_amc_id ? Number(form.vendor_amc_id) : null,
                visit_type: form.vendor_amc_id ? form.visit_type : (form.visit_type === 'SCHEDULED' ? 'AD_HOC' : form.visit_type),
                rating: form.rating ? Number(form.rating) : null,
            };
            delete body.visit_id;
            if (form.visit_id) {
                await axiosInstance.put(apiEndpoints.VENDOR_VISIT_DETAIL_API.replace(':id', form.visit_id), body);
                toast.success('Visit updated');
            } else {
                await axiosInstance.post(apiEndpoints.VENDOR_VISITS_API, body);
                toast.success('Visit recorded');
            }
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Could not save the visit');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-p-4" onClick={onClose}>
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-2xl tw-max-h-[92vh] tw-flex tw-flex-col" onClick={e => e.stopPropagation()}>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">{form.visit_id ? 'Edit Vendor Visit' : 'Log Vendor Visit'}</h2>
                    <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500"><Icon icon="mdi:close" className="tw-text-lg" /></button>
                </div>
                <div className="tw-p-5 tw-space-y-3 tw-overflow-y-auto">
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                        <div>
                            <label className={labelClass}>Vendor <span className="tw-text-red-500">*</span></label>
                            <select value={form.vendor_id} onChange={e => { set('vendor_id', e.target.value); set('vendor_amc_id', ''); }} className={inputClass}>
                                <option value="">Select vendor</option>
                                {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.business_name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Under AMC</label>
                            <select value={form.vendor_amc_id} onChange={e => set('vendor_amc_id', e.target.value)} className={inputClass} disabled={!form.vendor_id}>
                                <option value="">No AMC — ad-hoc visit</option>
                                {amcs.map(a => (
                                    <option key={a.vendor_amc_id} value={a.vendor_amc_id}>
                                        {a.amc_type} · {scheduleLabel(a.service_schedule)}{a.description ? ` · ${a.description}` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Visit Date <span className="tw-text-red-500">*</span></label>
                            <input type="date" value={form.visit_date} max={localDateString()} onChange={e => set('visit_date', e.target.value)} className={inputClass} />
                        </div>
                        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                            <div>
                                <label className={labelClass}>Time In</label>
                                <input type="time" value={form.check_in_time} onChange={e => set('check_in_time', e.target.value)} className={inputClass} />
                            </div>
                            <div>
                                <label className={labelClass}>Time Out</label>
                                <input type="time" value={form.check_out_time} onChange={e => set('check_out_time', e.target.value)} className={inputClass} />
                            </div>
                        </div>
                        <div>
                            <label className={labelClass}>Visit Type</label>
                            <select value={form.visit_type} onChange={e => set('visit_type', e.target.value)} className={inputClass}>
                                {form.vendor_amc_id && <option value="SCHEDULED">Scheduled (AMC)</option>}
                                <option value="AD_HOC">Ad-hoc / On call</option>
                                <option value="EMERGENCY">Emergency</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Outcome</label>
                            <select value={form.status} onChange={e => set('status', e.target.value)} className={inputClass}>
                                <option value="COMPLETED">Completed</option>
                                <option value="PARTIAL">Partially done</option>
                                <option value="MISSED">Vendor did not turn up</option>
                                <option value="CANCELLED">Cancelled</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Technician</label>
                            <input value={form.technician_name} maxLength={120} onChange={e => set('technician_name', e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>Technician Phone</label>
                            <input value={form.technician_phone} maxLength={20} onChange={e => set('technician_phone', e.target.value.replace(/[^\d+ -]/g, ''))} className={inputClass} />
                        </div>
                    </div>
                    <div>
                        <label className={labelClass}>Work Done</label>
                        <textarea rows={2} value={form.work_done} onChange={e => set('work_done', e.target.value)} className={inputClass} placeholder="e.g. Serviced both lift motors, replaced door sensor" />
                    </div>
                    <div>
                        <label className={labelClass}>Issues Found / Follow-up</label>
                        <textarea rows={2} value={form.issues_found} onChange={e => set('issues_found', e.target.value)} className={inputClass} />
                    </div>
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                        <div>
                            <label className={labelClass}>Attended / Verified by</label>
                            <input value={form.verified_by} maxLength={120} onChange={e => set('verified_by', e.target.value)} className={inputClass} placeholder="e.g. Supervisor name" />
                        </div>
                        <div>
                            <label className={labelClass}>Rating</label>
                            <div className="tw-flex tw-gap-0.5 tw-pt-1">
                                {[1, 2, 3, 4, 5].map(n => (
                                    <button type="button" key={n} onClick={() => set('rating', form.rating === n ? '' : n)} className="tw-p-0.5">
                                        <Icon icon={Number(form.rating) >= n ? 'mdi:star' : 'mdi:star-outline'} className="tw-text-lg tw-text-amber-400" />
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className={labelClass}>Remarks</label>
                            <input value={form.remarks} maxLength={500} onChange={e => set('remarks', e.target.value)} className={inputClass} />
                        </div>
                    </div>
                </div>
                <div className="tw-flex tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100">
                    <button onClick={onClose} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 hover:tw-bg-gray-50">Cancel</button>
                    <button onClick={submit} disabled={saving} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-teal-600 hover:tw-bg-teal-700 disabled:tw-opacity-50">
                        {saving ? 'Saving…' : 'Save Visit'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const VendorVisits = () => {
    const { can } = usePermissions();
    const canEdit = can(PERMISSIONS.VENDOR_UPDATE);
    const [month, setMonth] = useState(localDateString().slice(0, 7));
    const [tracker, setTracker] = useState(null);
    const [visits, setVisits] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vendorFilter, setVendorFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { month };
            if (vendorFilter) params.vendor_id = vendorFilter;
            const [t, v] = await Promise.all([
                axiosInstance.get(apiEndpoints.VENDOR_VISITS_MONTHLY_API, { params: { month } }),
                axiosInstance.get(apiEndpoints.VENDOR_VISITS_API, { params: { ...params, limit: 200 } }),
            ]);
            setTracker(t?.data || null);
            setVisits(Array.isArray(v?.data) ? v.data : []);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to load vendor visits');
        } finally {
            setLoading(false);
        }
    }, [month, vendorFilter]);

    useEffect(() => { load(); }, [load]);
    usePageRefresh(load);

    useEffect(() => {
        axiosInstance.get(apiEndpoints.VENDOR_VISIT_VENDORS_API)
            .then(res => setVendors(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setVendors([]));
    }, []);

    const lines = useMemo(() => (tracker?.lines || [])
        .filter(l => !vendorFilter || String(l.vendor.vendor_id) === String(vendorFilter)), [tracker, vendorFilter]);

    const openNew = (line) => {
        const today = localDateString();
        const inMonth = today.startsWith(month) ? today : `${month}-01`;
        setModal({
            ...EMPTY_FORM,
            vendor_id: line ? String(line.vendor.vendor_id) : (vendorFilter || ''),
            vendor_amc_id: line?.amc ? String(line.amc.vendor_amc_id) : '',
            visit_type: line?.amc ? 'SCHEDULED' : 'AD_HOC',
            visit_date: inMonth > today ? today : inMonth,
        });
    };

    const openEdit = (v) => setModal({
        ...EMPTY_FORM,
        ...Object.fromEntries(Object.entries(v).filter(([k]) => k in EMPTY_FORM || k === 'visit_id').map(([k, val]) => [k, val ?? ''])),
        vendor_id: String(v.vendor_id),
        vendor_amc_id: v.vendor_amc_id ? String(v.vendor_amc_id) : '',
        check_in_time: fmtTime(v.check_in_time),
        check_out_time: fmtTime(v.check_out_time),
    });

    const confirmDelete = async () => {
        try {
            await axiosInstance.delete(apiEndpoints.VENDOR_VISIT_DETAIL_API.replace(':id', deleteTarget.visit_id));
            toast.success('Visit deleted');
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Could not delete');
        }
    };

    const totals = tracker?.totals || {};
    const cards = [
        { label: 'Visits due', value: totals.expected ?? 0, icon: 'mdi:calendar-check-outline', color: 'tw-bg-teal-500' },
        { label: 'Done', value: totals.done ?? 0, icon: 'mdi:check-circle-outline', color: 'tw-bg-emerald-500' },
        { label: tracker?.month_over ? 'Missed' : 'Pending', value: tracker?.month_over ? (totals.missed ?? 0) : (totals.pending ?? 0), icon: tracker?.month_over ? 'mdi:close-circle-outline' : 'mdi:clock-outline', color: tracker?.month_over ? 'tw-bg-rose-500' : 'tw-bg-amber-500' },
        { label: 'Visits logged', value: totals.visits_logged ?? 0, icon: 'mdi:clipboard-text-outline', color: 'tw-bg-indigo-500' },
    ];

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-4">
                <div>
                    <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Vendor Visits</h1>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Monthly AMC service visits and ad-hoc vendor calls</p>
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <div className="tw-flex tw-items-center tw-bg-white tw-border tw-border-gray-200 tw-rounded-md">
                        <button onClick={() => setMonth(m => shiftMonth(m, -1))} className="tw-p-1.5 hover:tw-bg-gray-50" title="Previous month"><Icon icon="mdi:chevron-left" /></button>
                        <input type="month" value={month} onChange={e => e.target.value && setMonth(e.target.value)} className="tw-text-xs tw-px-1 tw-py-1 tw-border-0 focus:tw-outline-none" />
                        <button onClick={() => setMonth(m => shiftMonth(m, 1))} className="tw-p-1.5 hover:tw-bg-gray-50" title="Next month"><Icon icon="mdi:chevron-right" /></button>
                    </div>
                    <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} className="tw-text-xs tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-bg-white">
                        <option value="">All vendors</option>
                        {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.business_name}</option>)}
                    </select>
                    {canEdit && (
                        <button onClick={() => openNew(null)} className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-teal-600 hover:tw-bg-teal-700">
                            <Icon icon="mdi:plus" /> Log Visit
                        </button>
                    )}
                </div>
            </div>

            {/* Totals */}
            <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 tw-mb-4">
                {cards.map(c => (
                    <div key={c.label} className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-100 tw-shadow-sm tw-px-3 tw-py-2.5 tw-flex tw-items-center tw-gap-2.5">
                        <div className={`tw-w-8 tw-h-8 tw-rounded-lg ${c.color} tw-flex tw-items-center tw-justify-center`}><Icon icon={c.icon} className="tw-text-white tw-text-sm" /></div>
                        <div>
                            <p className="tw-text-base tw-font-bold tw-text-gray-800">{c.value}</p>
                            <p className="tw-text-[11px] tw-text-gray-500">{c.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Monthly tracker */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-mb-4">
                <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">{monthLabel(month)} — AMC visit tracker</h2>
                    <p className="tw-text-[11px] tw-text-gray-500">Expected visits come from each active AMC's service schedule.</p>
                </div>
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                {['Vendor', 'AMC / Schedule', 'Expected', 'Done', 'Last visit', 'Status', ''].map(h => (
                                    <th key={h || 'a'} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={7} className="tw-text-center tw-py-10"><div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-b-2 tw-border-teal-600 tw-mx-auto" /></td></tr>
                            ) : lines.length === 0 ? (
                                <tr><td colSpan={7} className="tw-text-center tw-py-10 tw-text-gray-400">No active AMCs or vendor visits for this month.</td></tr>
                            ) : lines.map((l, i) => {
                                const st = TRACKER_STATUS[l.status] || TRACKER_STATUS.NOT_DUE;
                                return (
                                    <tr key={`${l.kind}-${l.amc?.vendor_amc_id || l.vendor.vendor_id}-${i}`} className="tw-border-b tw-border-gray-50">
                                        <td className="tw-px-3 tw-py-2.5">
                                            <div className="tw-font-medium tw-text-gray-800">{l.vendor.business_name || `Vendor #${l.vendor.vendor_id}`}</div>
                                            <div className="tw-text-[10px] tw-text-gray-400">{[l.vendor.contact_person, l.vendor.phone].filter(Boolean).join(' · ')}</div>
                                        </td>
                                        <td className="tw-px-3 tw-py-2.5 tw-text-gray-600">
                                            {l.amc ? (
                                                <>
                                                    <div>{l.amc.amc_type}</div>
                                                    <div className={`tw-text-[10px] ${VALID_SCHEDULES.includes(l.amc.service_schedule) ? 'tw-text-gray-400' : 'tw-text-amber-600'}`}>
                                                        {scheduleLabel(l.amc.service_schedule)} · till {fmtDate(l.amc.amc_ends)}
                                                    </div>
                                                </>
                                            ) : <span className="tw-text-gray-400">No AMC</span>}
                                        </td>
                                        <td className="tw-px-3 tw-py-2.5 tw-text-gray-700">{l.expected}</td>
                                        <td className="tw-px-3 tw-py-2.5 tw-text-gray-700">{l.done}</td>
                                        <td className="tw-px-3 tw-py-2.5 tw-text-gray-600 tw-whitespace-nowrap">{fmtDate(l.last_visit_date)}</td>
                                        <td className="tw-px-3 tw-py-2.5">
                                            <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${st.cls}`}>
                                                <Icon icon={st.icon} className="tw-text-xs" />{st.label}
                                            </span>
                                        </td>
                                        <td className="tw-px-3 tw-py-2.5 tw-text-right">
                                            {canEdit && (l.status === 'PENDING' || l.status === 'MISSED' || l.status === 'NOT_DUE') && (
                                                <button onClick={() => openNew(l)} className="tw-text-[11px] tw-font-medium tw-text-teal-700 hover:tw-underline tw-whitespace-nowrap">Log visit</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Visit log */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">Visit log — {monthLabel(month)}</h2>
                </div>
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                {['Date', 'Vendor', 'Type', 'Time', 'Technician', 'Work done', 'Outcome', 'Rating', ''].map(h => (
                                    <th key={h || 'a'} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && visits.length === 0 && (
                                <tr><td colSpan={9} className="tw-text-center tw-py-10 tw-text-gray-400">No visits logged this month.</td></tr>
                            )}
                            {visits.map(v => (
                                <tr key={v.visit_id} className="tw-border-b tw-border-gray-50 tw-align-top">
                                    <td className="tw-px-3 tw-py-2.5 tw-whitespace-nowrap tw-text-gray-700">{fmtDate(v.visit_date)}</td>
                                    <td className="tw-px-3 tw-py-2.5">
                                        <div className="tw-font-medium tw-text-gray-800">{v.vendor?.business_name}</div>
                                        {v.amc && <div className="tw-text-[10px] tw-text-gray-400">{v.amc.amc_type}</div>}
                                    </td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-gray-600">{pretty(v.visit_type)}</td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-gray-600 tw-whitespace-nowrap">{[fmtTime(v.check_in_time), fmtTime(v.check_out_time)].filter(Boolean).join(' – ') || '—'}</td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-gray-600">
                                        <div>{v.technician_name || '—'}</div>
                                        {v.technician_phone && <div className="tw-text-[10px] tw-text-gray-400">{v.technician_phone}</div>}
                                    </td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-gray-600 tw-max-w-[260px]">
                                        <div className="tw-whitespace-pre-wrap tw-break-words">{v.work_done || '—'}</div>
                                        {v.issues_found && <div className="tw-text-[10px] tw-text-amber-700 tw-mt-0.5">Issues: {v.issues_found}</div>}
                                    </td>
                                    <td className="tw-px-3 tw-py-2.5">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${VISIT_STATUS[v.status] || ''}`}>{pretty(v.status)}</span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2.5 tw-whitespace-nowrap tw-text-amber-400">{v.rating ? '★'.repeat(v.rating) : <span className="tw-text-gray-300">—</span>}</td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-right tw-whitespace-nowrap">
                                        {canEdit && (
                                            <>
                                                <button onClick={() => openEdit(v)} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500" title="Edit"><Icon icon="mdi:pencil-outline" /></button>
                                                <button onClick={() => setDeleteTarget(v)} className="tw-p-1 tw-rounded hover:tw-bg-rose-50 tw-text-rose-500" title="Delete"><Icon icon="mdi:trash-can-outline" /></button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {modal && (
                <VisitModal initial={modal} vendors={vendors} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
            )}

            {deleteTarget && (
                <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-p-4" onClick={() => setDeleteTarget(null)}>
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-sm tw-p-5" onClick={e => e.stopPropagation()}>
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Delete this visit?</h3>
                        <p className="tw-text-xs tw-text-gray-600 tw-mt-2">{deleteTarget.vendor?.business_name} on {fmtDate(deleteTarget.visit_date)}</p>
                        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
                            <button onClick={() => setDeleteTarget(null)} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200">Cancel</button>
                            <button onClick={confirmDelete} className="tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorVisits;
